import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

Vue.use(Vuex)

import {
  BreadCrumb,
  Warnings,
  ColorScheme,
  FileSystemConfig,
  NavigationItem,
  Status,
  VisualizationPlugin,
  FavoriteLocation,
} from '@/Globals'

import fileSystems from '@/fileSystemConfig'
import { MAP_STYLES_ONLINE, MAP_STYLES_OFFLINE } from '@/Globals'

import { uploadFile, deleteFile } from './js/api'
import { int } from '~/nerdamer'

// ----------------------------------------
// ViewState has tricky logic, to handle cold-start, view-start,
// and interactive motion.
// Booleans to handle the various situations:
// ---------------------------------------------------------------------
// startup: true for the app default (here). views will override this
// initial: set to true for a view's first map request. This will be
//          honored if the app is in startup state, but will be ignored
//          if NOT in startup state, because that means a different view
//          has already set the map and we don't want it to be "jarring"
// jump:    Force jump the view to new location no matter what.
// ---------------------------------------------------------------------
const initialViewState = () => {
  return {
    // Set your startup city long/lat here!
    startup: true,
    longitude: 13.45,
    latitude: 52.5,
    zoom: 7,
    // longitude: -122.435,
    // latitude: 37.75,
    // zoom: 7,
    pitch: 0,
    bearing: 0,
    maxZoom: 24,
  }
}

const initialLeftSection = localStorage ? localStorage.getItem('activeLeftSection') || '' : ''

console.log('INITIAL LEFT SECTION', initialLeftSection)
export default new Vuex.Store({
  state: {
    app: 'SimWrapper',
    debug: false,
    authAttempts: 0,
    breadcrumbs: [] as BreadCrumb[],
    credentials: { fake: 'fake' } as { [url: string]: string },
    dashboardWidth: '',
    activeLeftSection: initialLeftSection,
    isFullScreen: false,
    isFullWidth: true,
    isShowingLeftBar: true,
    isShowingLeftStrip: true,
    isShowingFilesTab: true,
    isDarkMode: true,
    isInitialViewSet: false,
    favoriteLocations: [] as FavoriteLocation[],
    fileHandleAccessRequests: [] as any[],
    leftNavItems: null as null | {
      top: NavigationItem[]
      middle: NavigationItem[]
      bottom: NavigationItem[]
    },
    mapStyles: MAP_STYLES_ONLINE,
    needLoginForUrl: '',
    statusErrors: [] as Warnings[],
    statusWarnings: [] as Warnings[],
    statusMessage: 'Loading',
    svnProjects: fileSystems,
    visualizationTypes: new Map() as Map<string, VisualizationPlugin>,
    colorScheme: ColorScheme.DarkMode,
    locale: 'en',
    localFileHandles: [] as any[],
    localURLShortcuts: {} as { [id: string]: FileSystemConfig },
    numLocalFileSystems: 0,
    runFolders: {} as { [root: string]: any[] },
    runFolderCount: 0,
    resizeEvents: 0,
    windowTitle: '',
    topNavItems: null as null | {
      fileSystem: FileSystemConfig
      subfolder: string
      left: NavigationItem[]
      right: NavigationItem[]
    },
    viewState: initialViewState() as {
      longitude: number
      latitude: number
      bearing: number
      pitch: number
      zoom: number
      center?: number[]
      jump?: boolean
      initial?: boolean
      startup?: boolean
    },
    
    // DisitalSandBox
    // ログイン状態の保持
    isLoggedIn: false,
    JMDS_JWT: null,
    
    // シミュレータ情報
    simulatorId: '',
    simulatorMetaData: '',
    isLipt: false,
    isQgis: false,
    
    // データメタデータ情報
    dataMetaData: [] = new Array<string>(),
    
    // シミュレータ実行結果
    executionResult: '',
    
    currentUploadFileName: [] = new Array<string>(),
    fileNameMetaData: [] = new Array<string>(),
    uploadFileName: [] = new Array<string>(),
    selectedPrefecture: '',
    selectedMunicipalities: [] = new Array<string>(),
    selectedMunicipalitiesArea: [] = new Array<string>(),
    selectedLocations: [] = new Array<string>(),

    currentValue: [] = new Array<string>(),
    currentPrefecture: '',
    currentMunicipalities: [] = new Array<string>(),
    currentMunicipalitiesArea: [] = new Array<string>(),
    currentLocations: [] = new Array<string>(),
    distanceBusValue: 500,
    selectedBusUnit: 0,
    distanceTrainValue: 500,
    selectedTrainUnit: 0,
    
    // ステータス保持
    processStatus: 0,
    fileUploadstatus: 'idle' as 'idle' | 'uploading',
  },

  mutations: {
    updateRunFolders(
      state,
      value: { number: number; folders: { [root: string]: { path: string }[] } }
    ) {
      state.runFolderCount = value.number
      state.runFolders = value.folders
    },
    requestLogin(state, value: string) {
      state.needLoginForUrl = value
    },
    registerPlugin(state, value: VisualizationPlugin) {
      // console.log('PLUGIN:', value.kebabName)
      state.visualizationTypes.set(value.kebabName, value)
    },
    setActiveLeftSection(state, value: string) {
      state.activeLeftSection = value
      if (localStorage) localStorage.setItem('activeLeftSection', value)
    },
    setBreadCrumbs(state, value: BreadCrumb[]) {
      state.breadcrumbs = value
    },
    setCredentials(state, value: { url: string; username: string; pw: string }) {
      const creds = btoa(`${value.username}:${value.pw}`)
      state.credentials[value.url] = creds
      state.authAttempts++
    },
    setFullScreen(state, value: boolean) {
      state.isFullScreen = value
    },
    setLeftNavItems(
      state,
      value: { top: NavigationItem[]; middle: NavigationItem[]; bottom: NavigationItem[] }
    ) {
      state.leftNavItems = value
    },
    setShowFilesTab(state, value: boolean) {
      state.isShowingFilesTab = value
    },
    setShowLeftBar(state, value: boolean) {
      state.isShowingLeftBar = value
    },
    setShowLeftStrip(state, value: boolean) {
      state.isShowingLeftStrip = value
    },
    setTopNavItems(
      state,
      value: {
        left: NavigationItem[]
        right: NavigationItem[]
        fileSystem: FileSystemConfig
        subfolder: string
      }
    ) {
      state.topNavItems = value
    },

    setMapStyles(
      state,
      value: { light: string; dark: string; transparentLight: string; transparentDark: string }
    ) {
      state.mapStyles = value
    },
    setMapCamera(
      state,
      value: {
        longitude: number
        latitude: number
        bearing: number
        pitch: number
        zoom: number
        center?: number[]
        jump?: boolean
        initial?: boolean
        startup?: boolean
      }
    ) {
      let honorIt = false

      // always honor a jump request
      if (value.jump) honorIt = true
      // honor an initial request IFF we are in startup state
      if (value.initial && !state.isInitialViewSet) honorIt = true
      // honor a move request -- must not have initial or startup
      if (!value.initial && !value.startup) honorIt = true

      if (honorIt) {
        // remove logic, just keep camera settings
        const { jump, startup, initial, ...camera } = value
        state.viewState = camera
        state.isInitialViewSet = true
      } else {
        // console.log('(ignored')
      }
    },
    error(state, value: string) {
      console.error('store.ts: NOT SUPPOSED TO BE HERE!' + value)
      // if (
      //   !state.statusErrors.length ||
      //   state.statusErrors[state.statusErrors.length - 1].msg !== value
      // ) {
      //   state.statusErrors.push({ msg: value, desc: '' })
      //   state.isShowingLeftBar = true
      // }
    },
    setStatus(state, value: { type: Status; msg: string; desc?: string }) {
      console.error('store.ts: NOT SUPPOSED TO BE HERE!' + value)
      return
    },
    clearAllErrors(state) {
      state.statusErrors = []
      state.statusWarnings = []
    },
    resize(state) {
      state.resizeEvents += 1
    },
    setTheme(state, value: string) {
      state.colorScheme = value == 'light' ? ColorScheme.LightMode : ColorScheme.DarkMode

      console.log('THEME:', state.colorScheme)

      state.isDarkMode = state.colorScheme === ColorScheme.DarkMode

      if (localStorage) localStorage.setItem('colorscheme', state.colorScheme)
      document.body.style.backgroundColor =
        state.colorScheme === ColorScheme.LightMode ? '#edebe4' : '#2d3133'
    },
    rotateColors(state) {
      state.colorScheme =
        state.colorScheme === ColorScheme.DarkMode ? ColorScheme.LightMode : ColorScheme.DarkMode

      console.log('THEME:', state.colorScheme)

      state.isDarkMode = state.colorScheme === ColorScheme.DarkMode

      if (localStorage) localStorage.setItem('colorscheme', state.colorScheme)
      document.body.style.backgroundColor =
        state.colorScheme === ColorScheme.LightMode ? '#edebe4' : '#2d3133'
    },
    setLocale(state, value: string) {
      state.locale = value.toLocaleLowerCase()
      if (localStorage) localStorage.setItem('locale', state.locale)
    },
    addLocalFileSystem(state, value: any) {
      state.localFileHandles.unshift(value)
      state.numLocalFileSystems += 1
    },
    setLocalFileSystem(state, value: any) {
      state.localFileHandles = value
    },
    setURLShortcuts(state, shortcuts: { [id: string]: FileSystemConfig }) {
      state.localURLShortcuts = shortcuts
      // do some fancy replacing in case user specifically overwrote things
      const unique = state.svnProjects.filter(root => !(root.slug in shortcuts))
      state.svnProjects = [...Object.values(shortcuts), ...unique]
    },
    removeURLShortcut(state, shortcut: string) {
      delete state.localURLShortcuts[shortcut]
      const trimmed = state.svnProjects.filter(root => root.slug !== shortcut)
      state.svnProjects = trimmed

      try {
        const KEY = 'projectShortcuts'
        let existingRoot = localStorage ? localStorage.getItem(KEY) || ('{}' as any) : {}

        let roots = JSON.parse(existingRoot)
        delete roots[shortcut]
        console.log('NEW ROOTS', roots)

        if (localStorage) localStorage.setItem(KEY, JSON.stringify(roots))
      } catch (e) {
        // you failed
        console.error('' + e)
      }
    },

    setFavorites(state, favorites: FavoriteLocation[]) {
      state.favoriteLocations = favorites.map(f => {
        if (!f.fullPath) f.fullPath = `${f.root}/${f.subfolder}/${f.file || ''}`
        return f
      })
    },
    addFavorite(state, favorite: FavoriteLocation) {
      if (!favorite.fullPath)
        favorite.fullPath = `${favorite.root}${favorite.subfolder}/${favorite.file || ''}`

      // overwrite if user already has it
      const exists = state.favoriteLocations.findIndex(f => favorite.fullPath === f.fullPath)
      if (exists > -1) {
        state.favoriteLocations[exists] = favorite
      } else {
        state.favoriteLocations.push(favorite)
      }

      state.favoriteLocations.sort((a, b) => (a.label < b.label ? -1 : 1))
      state.favoriteLocations = [...state.favoriteLocations]

      try {
        if (localStorage)
          localStorage.setItem('favoriteLocations', JSON.stringify(state.favoriteLocations))
      } catch (e) {
        console.error('' + e)
      }
    },
    removeFavorite(state, favorite: FavoriteLocation) {
      if (!favorite.fullPath)
        favorite.fullPath = `${favorite.root}/${favorite.subfolder}/${favorite.file || ''}`

      const exists = state.favoriteLocations.findIndex(f => favorite.fullPath === f.fullPath)
      if (exists > -1) state.favoriteLocations.splice(exists, 1)

      try {
        if (localStorage)
          localStorage.setItem('favoriteLocations', JSON.stringify(state.favoriteLocations))
      } catch (e) {
        console.error('' + e)
      }
    },
    setFileHandleForPermissionRequest(state, handle: any) {
      state.fileHandleAccessRequests.push(handle)
    },
    clearFileHandlePermissionRequests(state) {
      state.fileHandleAccessRequests = []
    },
    toggleShowLeftBar(state) {
      state.isShowingLeftBar = !state.isShowingLeftBar
    },
    setDashboardWidth(state, value: string) {
      state.dashboardWidth = value
    },
    setFullWidth(state, value: boolean) {
      state.isFullWidth = value
    },
    toggleFullWidth(state) {
      state.isFullWidth = !state.isFullWidth
    },
    setWindowTitle(state, title: string) {
      if (title !== state.windowTitle) {
        state.windowTitle = title
        document.title = title ? title + ' - SimWrapper' : 'SimWrapper'
      }
    },
    
    // DigitalSandBox
    setIsLoggedInActions(state, isLoggedIn: boolean) {
      state.isLoggedIn = isLoggedIn;
    },
    setJMDS_JWTActions(state, jmdsJwt) {
      state.JMDS_JWT = jmdsJwt;
    },
    setSimulatorIdActions(state, simulatorId: string) {
      state.simulatorId = simulatorId;
    },
    setSimulatorMetaDataActions(state, simulatorMetaData){
      state.simulatorMetaData = simulatorMetaData;
    },
    setIsLiptActions(state, isLipt) {
      state.isLipt = isLipt;
    },
    setIsQgisActions(state, isQgis) {
      state.isQgis = isQgis;
    },
    
    // 選択したデータの保持
    addOrUpdateDataMetaDataActions(state, selectedData: string) {
      state.dataMetaData.push(selectedData);
    },
    removeDataMetaDataActions(state, title: string) {
      state.dataMetaData = state.dataMetaData.filter((d: string) => d.title !== title);
    },
    addOrUpdateDataMetaDataDecisionActions(state, selectedData) {
      state.dataMetaData = selectedData;
    },
    setCurrentValueActions(state, currentValue){
      state.currentValue = currentValue;
    },
    initializeDataMetaDataActions(state, payload) {
      state.dataMetaData = payload;
    },
    
    // 選択されている都道府県
    setSelectedPrefectureActions(state, prefecture: string) {
      state.selectedPrefecture = prefecture;
    },
    
    // 選択されている自治体コード一覧
    setSelectedMunicipalitiesActions(state, municipalities: string) {
      if (!state.selectedMunicipalities.includes(municipalities)) {
        state.selectedMunicipalities.push(municipalities);
      }
    },
    removeSelectedMunicipalitiesActions(state, municipalities: string) {
      state.selectedMunicipalities = state.selectedMunicipalities.filter((m: string) => m !== municipalities);
    },
    
    // 選択されている市区町村一覧
    setSelectedMunicipalitiesAreaActions(state, municipalities: string) {
      if (!state.selectedMunicipalitiesArea.includes(municipalities)) {
        state.selectedMunicipalitiesArea.push(municipalities);
      }
    },
    removeSelectedMunicipalitiesAreaActions(state, municipalities: string) {
      state.selectedMunicipalitiesArea = state.selectedMunicipalitiesArea.filter((m: string) => m !== municipalities);
    },
    
    // 都道府県と市区町村の配列
    addOrUpdateLocationActions(state, selectedData: string) {
      const index = state.selectedLocations.findIndex((location: string) => location.prefecture === selectedData.prefecture);
      if (index >= 0) {
        state.selectedLocations[index].municipalities = selectedData.municipalities; 
      } else {
        state.selectedLocations.push(selectedData);
      }
    },
    removeLocationActions(state, prefecture: string) {
      state.selectedLocations = state.selectedLocations.filter((location: string) => location.prefecture !== prefecture);
    },
    
    // 実行結果の保持、画面制御ステータスの保持
    setExecutionResultActions(state, executionResult){
      state.executionResult = executionResult;
    },
    setProcessStatusActions(state, processStatus: number){
      state.processStatus = processStatus;
    },
    
    // 前回値の設定(都道府県・市区町村)
    setCurrentPrefectureActions(state, prefecture) {
      state.currentPrefecture = prefecture;
    },
    setCurrentMunicipalitiesActions(state, municipalities) {
      state.currentMunicipalities = municipalities;
    },
    setCurrentMunicipalitiesAreaActions(state, municipalities) {
      state.currentMunicipalitiesArea = municipalities;
    },
    setCurrentLocationsActions(state, selectedData) {
      state.currentLocations = selectedData;
    },
    
    // 今回値の設定(都道府県・市区町村)
    setMunicipalitiesActions(state, municipalities) {
      state.selectedMunicipalities = municipalities;
    },
    setMunicipalitiesAreaActions(state, municipalities) {
      state.selectedMunicipalitiesArea = municipalities;
    },
    setLocationsActions(state, selectedData) {
      state.selectedLocations = selectedData;
    },

    clearMunicipalities(state) {
      state.selectedMunicipalities.length = 0;
    },
    clearMunicipalitiesArea(state) {
      state.selectedMunicipalitiesArea.length = 0;
    },
    clearLocations(state) {
      state.selectedLocations.length = 0;
    },

    setDistanceBusValueActions(state, distanceBusValue: int) {
      state.distanceBusValue = distanceBusValue;
    },
    setSelectedBusUnitActions(state, selectedBusUnit: int) {
      state.selectedBusUnit = selectedBusUnit;
    },
    setDistanceTrainValueActions(state, distanceTrainValue: int) {
      state.distanceTrainValue = distanceTrainValue;
    },
    setSelectedTrainUnitActions(state, selectedTrainUnit: int) {
      state.selectedTrainUnit = selectedTrainUnit;
    },
    
    // データ選択画面で持ちまわるアップロードファイル名
    addCurrentUploadFileNameActions(state, uploadFileName: string) {
      if (!state.currentUploadFileName.includes(uploadFileName)) {
        state.currentUploadFileName.push(uploadFileName);
        state.fileNameMetaData.push(uploadFileName);
        state.uploadFileName.push(uploadFileName);
      }
    },
    clearUploadFileNameActions(state) {
      state.uploadFileName = new Array<string>();
    },
    removeCurrentUploadFileNameActions(state, fileName: string) {
      state.currentUploadFileName = state.currentUploadFileName.filter(file => file !== fileName);
    },
    initializeCurrentUploadFileNameActions(state, payload) {
      state.currentUploadFileName = payload;
    },
    initializeFileNameMetaDataActions(state, payload) {
      state.fileNameMetaData = payload;
    },
    removeFileNameMetaDataActions(state, fileName: string) {
      state.fileNameMetaData = state.fileNameMetaData.filter(file => file !== fileName);
    },
    setFileUploadStatus(state, status: 'idle' | 'uploading') {
      state.fileUploadstatus = status;
    },
    
    changeDistanceBusValue(state, num) {
      if (num) state.distanceBusValue = num;
    },
    changeDistanceTrainValue(state, num) {
      if (num) state.distanceTrainValue = num;
    },
  },
  
  actions: {
    setIsLoggedIn({ commit }, isLoggedIn: boolean) {
      commit('setIsLoggedInActions', isLoggedIn);
    },
    setJMDS_JWT({ commit }) {
      let jmdsJwt = null;
        document.cookie.split('; ').forEach(cookie => {
          const [key, value] = cookie.split('=');
          if (key === 'JMDS_JWT') {
            jmdsJwt = decodeURIComponent(value);
          }
        });
      commit('setJMDS_JWTActions', jmdsJwt);
    },
    setSimulatorId({ commit }, simulatorId: string) {
      commit('setSimulatorIdActions', simulatorId);
    },
    setSimulatorMetaData({ commit }, simulatorMetaData){
      commit('setSimulatorMetaDataActions', simulatorMetaData);
    },
    setIsLipt({ commit }, isLipt: boolean) {
      commit('setIsLiptActions', isLipt);
    },
    setIsQgis({ commit }, isQgis: boolean) {
      commit('setIsQgisActions', isQgis);
    },
    
    // 選択したデータの保持
    setDataMetaData({ commit }, metaData: string) {
       commit('addOrUpdateDataMetaDataActions', metaData);
    },
    removeDataMetaData({ commit }, title: string) {
       commit('removeDataMetaDataActions', title);
    },
    setDataMetaDataDecision({ commit }, metaData: string) {
       commit('addOrUpdateDataMetaDataDecisionActions', metaData);
    },
    setCurrentValue({ commit }, currentValue){
      commit('setCurrentValueActions', currentValue);
    },
    initializeDataMetaData({ commit }, payload) {
       commit('initializeDataMetaDataActions', payload);
    },
    
    // 選択中の都道府県の保持、選択した市区町村の保持・削除
    setSelectedPrefecture({ commit }, prefecture: string) {
      commit('setSelectedPrefectureActions', prefecture);
    },
    setSelectedMunicipalities({ commit }, municipalities: string) {
      commit('setSelectedMunicipalitiesActions', municipalities);
    },
    removeSelectedMunicipalities({ commit }, municipalities: string) {
      commit('removeSelectedMunicipalitiesActions', municipalities);
    },
    setSelectedMunicipalitiesArea({ commit }, municipalities: string) {
      commit('setSelectedMunicipalitiesAreaActions', municipalities);
    },
    removeSelectedMunicipalitiesArea({ commit }, municipalities: string) {
      commit('removeSelectedMunicipalitiesAreaActions', municipalities);
    },
    
    // 今回値の設定
    setMunicipalities({ commit }, municipalities) {
      commit('setMunicipalitiesActions', municipalities);
    },
    setMunicipalitiesArea({ commit }, municipalities) {
      commit('setMunicipalitiesAreaActions', municipalities);
    },
    setLocations({ commit }, locations) {
      commit('setLocationsActions', locations);
    },
    
    // 選択した都道府県に対する市区町村の保持（都道府県と市区町村を対応させた状態で保持）
    addOrUpdateLocation({ commit }, selectedData: string) {
      commit('addOrUpdateLocationActions', selectedData);
    },
    removeLocation({ commit }, prefecture: string) {
      commit('removeLocationActions', prefecture);
    },
    
    // 前回値の設定
    setCurrentPrefecture({ commit }, prefecture) {
      commit('setCurrentPrefectureActions', prefecture);
    },
    setCurrentMunicipalities({ commit }, municipalities) {
      commit('setCurrentMunicipalitiesActions', municipalities);
    },
    setCurrentMunicipalitiesArea({ commit }, municipalities) {
      commit('setCurrentMunicipalitiesAreaActions', municipalities);
    },
    setCurrentLocations({ commit }, selectedData) {
      commit('setCurrentLocationsActions', selectedData);
    },

    
    setDistanceBusValue({ commit }, distanceBusValue: int) {
      commit('setDistanceBusValueActions', distanceBusValue);
    },
    setSelectedBusUnit({ commit }, selectedBusUnit: int) {
      commit('setSelectedBusUnitActions', selectedBusUnit);
    },
    setDistanceTrainValue({ commit }, distanceTrainValue: int) {
      commit('setDistanceTrainValueActions', distanceTrainValue);
    },
    setSelectedTrainUnit({ commit }, selectedTrainUnit: int) {
      commit('setSelectedTrainUnitActions', selectedTrainUnit);
    },
    
    // アップロードファイル

    async uploadFile({ commit }, { jmdsJwt, simulatorId, file }: { jmdsJwt:string; simulatorId: string; file: File }) {
      commit("setFileUploadStatus", "uploading");
      try {
        const fileNames = await uploadFile(jmdsJwt, simulatorId, file);
        if (Array.isArray(fileNames)) {
          fileNames.forEach(fileName => commit("addCurrentUploadFileNameActions", fileName));
        }
      } catch(error) {
        console.error("File upload is failure.", error);
        throw error;
      } finally {
        commit("setFileUploadStatus", "idle");
      }
    },

    async deleteFile({ commit }, { jmdsJwt, simulatorId, fileName }: { jmdsJwt:string; simulatorId: string; fileName: string }) {
      try {
        await deleteFile(jmdsJwt, simulatorId, fileName);
        commit("removeCurrentUploadFileNameActions", fileName);
      } catch (error) {
        console.error("File delete is failure.", error);
        throw error;
      }
    },
    clearUploadFileName({ commit }) {
      commit('clearUploadFileNameActions');
    },
    removeCurrentUploadFileName({ commit }, fileName: string) {
      commit('removeCurrentUploadFileNameActions', fileName);
    },
    initializeCurrentUploadFileName({ commit }, payload) {
      commit('initializeCurrentUploadFileNameActions', payload);
    },

    initializeFileNameMetaData({ commit }, payload) {
      commit('initializeFileNameMetaDataActions', payload);
    },

    removeFileNameMetaData({ commit }, fileName: string) {
       commit('removeFileNameMetaDataActions', fileName);
    },
    
    // 画面遷移時のステータス保持
    setProcessStatus({ commit }, status: number) {
      commit('setProcessStatusActions', status);
    },
    // シミュレータ実行結果の保持
    setExecutionResult({ commit }, executionResult){
      commit('setExecutionResultActions', executionResult);
    },
  },
  modules: {},
  getters: {
    mapStyle: state => {
      return state.isDarkMode ? state.mapStyles.dark : state.mapStyles.light
    },
    
    dataMetaData: state => {
      return state.dataMetaData
    },
    selectedPrefecture: state => {
      return state.selectedPrefecture
    },
    selectedMunicipalities: state => {
      return state.selectedMunicipalities 
    },
    distanceBusValue: state => state.distanceBusValue,
    distanceTrainValue: state => state.distanceTrainValue
  },
  plugins: [createPersistedState({
    key: 'Vuex',
    paths: [
        'isLoggedIn',
        'simulatorId',
        'simulatorMetaData',
        'dataMetaData',
        'selectedPrefecture',
        'selectedMunicipalities',
        'selectedMunicipalitiesArea',
        'selectedLocations',
        'currentValue',
        'currentPrefecture',
        'currentMunicipalities',
        'currentMunicipalitiesArea',
        'currentLocations',
        'currentUploadFileName',
        'processStatus',
        'fileUploadStatus'
    ],
    storage: window.sessionStorage
  })]
})
